import {unref} from 'vue';
import {GoingNewUtils} from 'xmgoing-utils';
import {usePageModelsStore} from '@/store/modules/pageModels';
import {totalFun} from '@/main-page/ctls/hooks/useVuexState';

const requiredFunCache = new Map<string, Function | null>();
const reportedErrors = new Set<string>();
const ROW_SCOPED_REG = /(^|[^\w$.])vuex\.record(?![\w$])/;

function reportError(e: any, vuexStr: string) {
  if (reportedErrors.has(vuexStr)) {
    return;
  }
  reportedErrors.add(vuexStr);
  console.error(e, '==requiredVuex==' + vuexStr);
}

function getRequiredFun(vuexStr: string) {
  if (!requiredFunCache.has(vuexStr)) {
    try {
      requiredFunCache.set(vuexStr, new Function('vuex', `return ${vuexStr}`));
    } catch (e) {
      reportError(e, vuexStr);
      requiredFunCache.set(vuexStr, null);
    }
  }
  return requiredFunCache.get(vuexStr);
}

/** 不在组件 setup 内调用时，store 按当前页面编码定位 */
function getPageState() {
  try {
    return unref(usePageModelsStore().state);
  } catch (e) {
    return {};
  }
}

function getRequiredVuex(col: any): string {
  const vuexStr = col?.requiredVuex ?? col?.props?.requiredVuex;
  return vuexStr && (vuexStr + '').trim() ? vuexStr + '' : '';
}

export function hasRequiredVuex(column: any) {
  return !!getRequiredVuex(unref(column));
}

/** 动态必填表达式引用了当前行（vuex.record）即为按行必填，否则为整列必填 */
export function isRowScopedRequired(column: any) {
  return ROW_SCOPED_REG.test(getRequiredVuex(unref(column)));
}

/**
 * 求某一格是否必填：有动态必填表达式时只看表达式，否则看静态开关。
 * 整列必填不传 record 也能求值；state 可传状态或取状态的函数，不传时自行取页面状态。
 */
export function resolveColumnRequired(column: any, record?: any, state?: any): boolean {
  const col = unref(column);
  const vuexStr = getRequiredVuex(col);
  if (!vuexStr) {
    return GoingNewUtils.parseBoolean(col?.required) || GoingNewUtils.parseBoolean(col?.props?.required);
  }
  const fun = getRequiredFun(vuexStr);
  if (!fun) {
    return false;
  }
  try {
    let pageState;
    if (state === undefined) {
      pageState = getPageState();
    } else {
      pageState = typeof state === 'function' ? state() : unref(state);
    }
    const row = unref(record) || {};
    return GoingNewUtils.parseBoolean(fun({record: row, state: pageState, total: totalFun}));
  } catch (e) {
    reportError(e, vuexStr);
    return false;
  }
}

/** 列头是否画红色 *：按行必填不画，整列必填与静态必填按结果画 */
export function showHeaderRequired(column: any, state?: any) {
  if (isRowScopedRequired(column)) {
    return false;
  }
  return resolveColumnRequired(column, undefined, state);
}

/** 在组件 setup 内取本页 store，返回绑定了页面状态的判定函数 */
export function useColumnRequired() {
  const pageStore: any = usePageModelsStore();
  const getState = () => unref(pageStore.state) || {};
  return {
    getState,
    resolveRequired: (column: any, record?: any) => resolveColumnRequired(column, record, getState),
    showHeaderRequired: (column: any) => showHeaderRequired(column, getState),
  };
}
